import React, { useEffect, useState } from 'react';
import { PrimaryButton, TextField, Text, Image, Stack } from '@fluentui/react';
import './RequestFeedback.scss';
import { usePostFeedback } from 'app/services/request-api';
import { FeedbackCategory } from 'app/enums/FeedbackCategory.enum';
import { appInsightsService } from 'app/utils/constants';
import fcIconTerrible from 'app/static/icons/fc_icon_terrible.svg';
import fcIconBad from 'app/static/icons/fc_icon_bad.svg';
import fcIconOkay from 'app/static/icons/fc_icon_okay.svg';
import fcIconGood from 'app/static/icons/fc_icon_good.svg';
import fcIconGreat from 'app/static/icons/fc_icon_great.svg';
import fcIconTerribleActive from 'app/static/icons/fc_icon_terrible_active.svg';
import fcIconBadActive from 'app/static/icons/fc_icon_bad_active.svg';
import fcIconOkayActive from 'app/static/icons/fc_icon_okay_active.svg';
import fcIconGoodActive from 'app/static/icons/fc_icon_good_active.svg';
import fcIconGreatActive from 'app/static/icons/fc_icon_great_active.svg';

interface SmileyIcons {
    great: { defaultSrc: string; activeSrc: string };
    good: { defaultSrc: string; activeSrc: string };
    okay: { defaultSrc: string; activeSrc: string };
    bad: { defaultSrc: string; activeSrc: string };
    terrible: { defaultSrc: string; activeSrc: string };
}

export type RequestFeedbackSubmittedDetails = {
    reaction: string;
    hasComment: boolean;
    commentLength: number;
};

interface RequestFeedbackProps {
    requestId: string;
    onSubmitted?: (details: RequestFeedbackSubmittedDetails) => void;
    icons?: SmileyIcons;
}
const RequestFeedback: React.FC<RequestFeedbackProps> = ({ requestId, onSubmitted, icons }) => {
    const [reaction, setReaction] = useState<string>('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const { mutate: postFeedback } = usePostFeedback();

    // Preload default icons for smoother UX
    useEffect(() => {
        const toPreload = [
            icons?.great?.activeSrc ?? fcIconGreatActive,
            icons?.good?.activeSrc ?? fcIconGoodActive,
            icons?.okay?.activeSrc ?? fcIconOkayActive,
            icons?.bad?.activeSrc ?? fcIconBadActive,
            icons?.terrible?.activeSrc ?? fcIconTerribleActive,
        ];
        toPreload.forEach((src) => {
            const img = document.createElement('img');
            img.src = src;
        });
    }, [icons]);

    // Log when feedback form is shown
    useEffect(() => {
        appInsightsService.logEvent('FeedbackFormShown', { requestId: String(requestId) });
    }, []);

    const getIconSrc = (name: string, active: boolean) => {
        const map: Record<string, { defaultSrc: string; activeSrc: string }> = {
            great: {
                defaultSrc: icons?.great?.defaultSrc ?? fcIconGreat,
                activeSrc: icons?.great?.activeSrc ?? fcIconGreatActive,
            },
            good: {
                defaultSrc: icons?.good?.defaultSrc ?? fcIconGood,
                activeSrc: icons?.good?.activeSrc ?? fcIconGoodActive,
            },
            okay: {
                defaultSrc: icons?.okay?.defaultSrc ?? fcIconOkay,
                activeSrc: icons?.okay?.activeSrc ?? fcIconOkayActive,
            },
            bad: {
                defaultSrc: icons?.bad?.defaultSrc ?? fcIconBad,
                activeSrc: icons?.bad?.activeSrc ?? fcIconBadActive,
            },
            terrible: {
                defaultSrc: icons?.terrible?.defaultSrc ?? fcIconTerrible,
                activeSrc: icons?.terrible?.activeSrc ?? fcIconTerribleActive,
            },
        };
        return active ? map[name].activeSrc : map[name].defaultSrc;
    };

    const handleSelectReaction = (name: string) => {
        // Only log when the selection actually changes
        if (reaction !== name) {
            appInsightsService.logEvent('FeedbackExperienceSelected', {
                requestId: String(requestId),
                experience: String(name),
            });
        }
        setReaction(name);
    };

    const renderSmiley = (name: string) => {
        const isActive = reaction === name;
        const imgId = `${name}-img`;
        return (
            <div
                role="button"
                aria-label={`${name} feedback${isActive ? ', selected' : ''}`}
                aria-pressed={isActive}
                tabIndex={0}
                onClick={() => handleSelectReaction(name)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelectReaction(name);
                }}
            >
                <Image
                    id={imgId}
                    src={getIconSrc(name, isActive)}
                    alt={name}
                    onMouseOver={(event) => {
                        (event.currentTarget as HTMLImageElement).src = getIconSrc(name, true);
                    }}
                    onMouseOut={(event) => {
                        if (!isActive) (event.currentTarget as HTMLImageElement).src = getIconSrc(name, false);
                    }}
                />
                <div>{name[0].toUpperCase() + name.substring(1)}</div>
            </div>
        );
    };

    const handleSubmit = async () => {
        if (!reaction && comment.trim() === '') {
            return;
        }
        setSubmitting(true);
        try {
            interface FeedbackData {
                feedbackMessage: string;
                experience: string;
                feedbackCategory: FeedbackCategory;
                requestId?: string;
            }
            // Follow FeedbackCard pattern; include default Category and RequestId.
            postFeedback({
                feedbackMessage: comment.trim(),
                experience: reaction,
                // Map category to FeedbackCategory: Request
                feedbackCategory: FeedbackCategory.Request,
                // Additional properties sent for request-context feedback.
                ...(requestId ? { requestId } : {})
            } as FeedbackData);
            setSubmitted(true);
            if (onSubmitted) {
                onSubmitted({
                    reaction,
                    hasComment: comment.trim().length > 0,
                    commentLength: comment.trim().length
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <Stack tokens={{ childrenGap: 24 }}>
                <Text>Thanks for your feedback!</Text>
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 24 }} className="request-feedback">
            <Stack tokens={{ childrenGap: 8 }}>
                <Text>How was your experience?</Text>
                <Stack horizontal tokens={{ childrenGap: 24 }}>
                    {renderSmiley('great')}
                    {renderSmiley('good')}
                    {renderSmiley('okay')}
                    {renderSmiley('bad')}
                    {renderSmiley('terrible')}
                </Stack>
            </Stack>
            {reaction && (
                <Stack tokens={{ childrenGap: 8 }}>
                    <Text>Please provide your feedback</Text>
                    <TextField
                        multiline
                        rows={3}
                        value={comment}
                        onChange={(_, v) => setComment(v ?? '')}
                        placeholder="Write your comments here..."
                    />
                </Stack>
            )}
            <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                <PrimaryButton text="Submit feedback" onClick={handleSubmit} disabled={submitting} />
            </Stack>

        </Stack>
    );
};

export default RequestFeedback;
